import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useOutletContext } from 'react-router-dom';
import { Settings, User, Lock, AlertCircle, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface OutletCtx { isUnlocked: boolean; }

export function SISettingsPage() {
  const { isUnlocked } = useOutletContext<OutletCtx>();
  const { user } = useAuth();
  const [fullName, setFullName] = useState('');
  const [plan, setPlan] = useState('Free — Early Access');
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from('users').select('full_name').eq('id', user.id).maybeSingle().then(({ data }) => {
      if (data?.full_name) setFullName(data.full_name);
      setLoading(false);
    });
    supabase.from('coaches').select('tier, si_unlocked').eq('user_id', user.id).maybeSingle().then(({ data }) => {
      if (data) {
        if (data.si_unlocked) {
          const tierMap: Record<string, string> = {
            basic: 'Core — $79/month',
            basic_plus: 'Professional — $129/month',
            advanced: 'Unlimited — $149/month',
          };
          setPlan(tierMap[data.tier] ?? 'Paid Account');
        }
      }
    });
  }, [user]);

  const handlePasswordChange = async () => {
    setPwError('');
    setPwSuccess(false);
    if (passwordForm.next !== passwordForm.confirm) { setPwError('Passwords do not match'); return; }
    if (passwordForm.next.length < 8) { setPwError('Password must be at least 8 characters'); return; }
    setChangingPw(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: passwordForm.next });
      if (error) throw error;
      setPwSuccess(true);
      setPasswordForm({ current: '', next: '', confirm: '' });
    } catch (err: any) {
      setPwError(err.message || 'Failed to update password');
    } finally {
      setChangingPw(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-10 rounded-xl">
            <Settings className="w-5 h-5 text-gray-50" />
          </div>
          <h1 className="text-2xl font-bold text-gray-80">Settings</h1>
        </div>
      </motion.div>

      {/* Account details */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-2xl border border-gray-20 p-6"
      >
        <div className="flex items-center gap-2 mb-5">
          <User className="w-4 h-4 text-gray-50" />
          <h2 className="text-sm font-semibold text-gray-80">Account Details</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-50 mb-1">Full Name</label>
            <div className="px-4 py-2.5 bg-brand-5 border border-gray-20 rounded-xl text-sm text-gray-60">
              {fullName || '—'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-50 mb-1">Email Address</label>
            <div className="px-4 py-2.5 bg-brand-5 border border-gray-20 rounded-xl text-sm text-gray-60">
              {user?.email}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Plan status */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-gray-20 p-6"
      >
        <h2 className="text-sm font-semibold text-gray-80 mb-4">Plan Status</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-base font-bold text-gray-80">{plan}</p>
            {!isUnlocked && (
              <p className="text-xs text-gray-40 mt-0.5">Your first report will be activated during the live walkthrough</p>
            )}
          </div>
          {!isUnlocked && (
            <a
              href="https://sessionintelligence.com/walkthrough"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-xs font-semibold transition-colors"
            >
              Join Walkthrough
            </a>
          )}
        </div>
      </motion.div>

      {/* Change password */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-2xl border border-gray-20 p-6"
      >
        <div className="flex items-center gap-2 mb-5">
          <Lock className="w-4 h-4 text-gray-50" />
          <h2 className="text-sm font-semibold text-gray-80">Change Password</h2>
        </div>
        {pwError && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 p-3 rounded-xl mb-4 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {pwError}
          </div>
        )}
        {pwSuccess && (
          <div className="flex items-center gap-2 bg-success-10 text-success-50 p-3 rounded-xl mb-4 text-sm">
            <Check className="w-4 h-4 flex-shrink-0" />
            Password updated successfully.
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-80 mb-1.5">New Password</label>
            <input
              type="password"
              value={passwordForm.next}
              onChange={e => setPasswordForm(p => ({ ...p, next: e.target.value }))}
              placeholder="Min. 8 characters"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-80 mb-1.5">Confirm New Password</label>
            <input
              type="password"
              value={passwordForm.confirm}
              onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
              placeholder="Re-enter new password"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
            />
          </div>
          <button
            onClick={handlePasswordChange}
            disabled={changingPw || !passwordForm.next}
            className="px-5 py-2.5 bg-gray-80 hover:bg-gray-60 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {changingPw ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
